import {
    calculatePercentile,
    calculateTScore,
    clamp,
    percentileToZ,
    tScoreToZ,
} from '@/utils/statistics';

export type ScoreMetric = 'z' | 't' | 'percentile';

export interface ScoreConversion {
    z: number;
    t: number;
    percentile: number;
    below: number;
    above: number;
}

export interface ScoreConversionResult {
    data: ScoreConversion | null;
    error?: string;
}

const DISPLAY_FORMATTER = new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

export function parseMetricInput(rawValue: string): number | null {
    const trimmed = rawValue.trim();

    if (
        !trimmed ||
        trimmed === '-' ||
        trimmed === ',' ||
        trimmed === '.' ||
        trimmed === '-,' ||
        trimmed === '-.'
    ) {
        return null;
    }

    const normalized = trimmed.replace(/\s+/g, '').replace(',', '.');
    const parsed = Number(normalized);

    return Number.isFinite(parsed) ? parsed : null;
}

export function resolveScoreConversion(
    metric: ScoreMetric,
    rawValue: string,
): ScoreConversionResult {
    const trimmed = rawValue.trim();

    if (!trimmed) {
        return { data: null };
    }

    const parsed = parseMetricInput(rawValue);

    if (parsed === null) {
        return {
            data: null,
            error: 'Digite um valor numerico valido usando virgula ou ponto decimal.',
        };
    }

    if (metric === 'percentile' && (parsed <= 0 || parsed >= 100)) {
        return {
            data: null,
            error: 'Para inverter o percentil em escore Z ou T, use um valor estritamente entre 0 e 100.',
        };
    }

    const z = metric === 'z' ? parsed : metric === 't' ? tScoreToZ(parsed) : percentileToZ(parsed);

    const percentile = calculatePercentile(z);

    return {
        data: {
            z,
            t: calculateTScore(z),
            percentile,
            below: percentile,
            above: 100 - percentile,
        },
    };
}

export function formatMetricValue(metric: ScoreMetric, value: number): string {
    if (metric === 'percentile') {
        return DISPLAY_FORMATTER.format(clamp(value, 0, 100));
    }

    return DISPLAY_FORMATTER.format(value);
}

export function formatPercentShare(value: number): string {
    return `${DISPLAY_FORMATTER.format(clamp(value, 0, 100))}%`;
}

export function getMetricLabel(metric: ScoreMetric): string {
    switch (metric) {
        case 'z':
            return 'Escore Z';
        case 't':
            return 'Escore T';
        case 'percentile':
            return 'Percentil';
    }
}

export const PRESET_Z_VALUES = [-2, -1, 0, 1, 2];
export const CHART_Z_DOMAIN: [number, number] = [-3.4, 3.4];
